import React from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  TextField,
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { getChainLogoUrl, getTokenLogoUrl } from './ChainLogos';

const KeyConfigForm = ({
  selectedToken,
  onTokenChange,
  keyRows,
  onUpdateRow,
  onAddRow,
  onRemoveRow,
  denomsByChain,
  allTokenSymbols,
  tokenLocked,
  chainsForToken,
  isDark,
  isMobile,
}) => {
  const borderColor = isDark ? '#262626' : '#e5e5e5';
  const subtleText = isDark ? '#a3a3a3' : '#525252';
  const subtleBg = isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)';
  const accentColor = isDark ? '#22c55e' : '#16a34a';

  return (
    <Box>
      {/* Token Family Selector */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          bgcolor: isDark ? '#111318' : '#f8f9fa',
          borderLeft: `3px solid ${accentColor}`,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, color: accentColor, textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 1.5 }}>
          Select Token
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel>Token</InputLabel>
          <Select
            value={selectedToken}
            onChange={(e) => onTokenChange(e.target.value)}
            label="Token"
            disabled={tokenLocked}
          >
            {allTokenSymbols.map((symbol) => (
              <MenuItem key={symbol} value={symbol}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getTokenLogoUrl(symbol) && (
                    <Box
                      component="img"
                      src={getTokenLogoUrl(symbol)}
                      alt={symbol}
                      sx={{ width: 20, height: 20, borderRadius: '50%' }}
                    />
                  )}
                  {symbol}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {tokenLocked && (
          <Typography variant="caption" sx={{ color: accentColor, mt: 0.5, display: 'block' }}>
            Token locked. Remove all keys to change.
          </Typography>
        )}
      </Box>

      {/* Key Rows */}
      {selectedToken && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Withdrawal Keys
            </Typography>
            <Button
              onClick={onAddRow}
              startIcon={<Add />}
              size="small"
              sx={{ color: accentColor, textTransform: 'none', fontWeight: 600 }}
            >
              Add Key
            </Button>
          </Box>

          {keyRows.map((row, index) => {
            const chainDenoms = denomsByChain[row.chain] || [];
            const seen = new Set();
            const filteredDenoms = chainDenoms
              .filter((d) => d.tokenSymbol === selectedToken)
              .filter((d) => {
                const key = `${d.denomination}-${d.tokenSymbol}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
              });

            return (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  mb: 1.5,
                  border: '1px solid',
                  borderColor,
                  borderLeft: `3px solid ${borderColor}`,
                  bgcolor: subtleBg,
                  position: 'relative',
                  '&:hover .remove-btn': { opacity: 1 },
                }}
              >
                <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1.5fr',
                      gap: 2,
                    }}
                  >
                    {/* Chain selector */}
                    <Box>
                      <Typography variant="caption" sx={{ color: subtleText, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.6rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Chain
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={row.chain}
                          onChange={(e) => onUpdateRow(index, 'chain', e.target.value)}
                          displayEmpty
                          renderValue={(v) => {
                            if (!v) return <Typography sx={{ color: subtleText, fontSize: '0.85rem' }}>Select...</Typography>;
                            const chain = chainsForToken.find((c) => c.chain_name === v);
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                {getChainLogoUrl(v) && (
                                  <Box component="img" src={getChainLogoUrl(v)} alt={v} sx={{ width: 16, height: 16, borderRadius: '50%' }} />
                                )}
                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{chain?.name || v}</Typography>
                              </Box>
                            );
                          }}
                        >
                          {chainsForToken.map((chain) => (
                            <MenuItem key={chain.chain_name} value={chain.chain_name}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {getChainLogoUrl(chain.chain_name) && (
                                  <Box component="img" src={getChainLogoUrl(chain.chain_name)} alt={chain.name} sx={{ width: 18, height: 18, borderRadius: '50%' }} />
                                )}
                                {chain.name}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Denomination selector */}
                    <Box>
                      <Typography variant="caption" sx={{ color: subtleText, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.6rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Amount
                      </Typography>
                      <FormControl fullWidth size="small">
                        <Select
                          value={row.denomination}
                          onChange={(e) => onUpdateRow(index, 'denomination', e.target.value)}
                          disabled={!row.chain}
                          displayEmpty
                          renderValue={(v) => {
                            if (!v) return <Typography sx={{ color: subtleText, fontSize: '0.85rem' }}>{row.chain ? 'Select...' : '—'}</Typography>;
                            return <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{v} {selectedToken}</Typography>;
                          }}
                        >
                          {filteredDenoms.length === 0 ? (
                            <MenuItem value="" disabled>
                              {row.chain ? 'No denominations' : 'Select chain first'}
                            </MenuItem>
                          ) : (
                            filteredDenoms.map((d) => (
                              <MenuItem key={`${d.denomination}-${d.tokenSymbol}`} value={d.denomination.toString()}>
                                {d.denomination} {selectedToken}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Recipient address */}
                    <Box>
                      <Typography variant="caption" sx={{ color: subtleText, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '0.6rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Recipient Address
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={
                          chainsForToken.find((c) => c.chain_name === row.chain)?.chainType === 'solana'
                            ? 'Solana address'
                            : '0x...'
                        }
                        value={row.recipientAddress || ''}
                        onChange={(e) => onUpdateRow(index, 'recipientAddress', e.target.value)}
                        disabled={!row.denomination}
                        InputProps={{
                          sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Remove button */}
                  {keyRows.length > 1 && (
                    <IconButton
                      className="remove-btn"
                      onClick={() => onRemoveRow(index)}
                      size="small"
                      sx={{
                        position: 'absolute',
                        right: -8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#fff',
                        bgcolor: '#ef4444',
                        width: 22,
                        height: 22,
                        opacity: isMobile ? 1 : 0,
                        transition: 'opacity 0.15s',
                        '&:hover': { bgcolor: '#dc2626' },
                      }}
                    >
                      <Close sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default KeyConfigForm;
